import { usePathname } from "next/navigation";
import React from "react";
import { create } from "zustand";
import { useShallow } from "zustand/shallow";

type RouteStore = {
  previousRoutes: string[];
  setPreviousRoutes: (routes: string[]) => void;
};

const useRouterStore = create<RouteStore>((set) => ({
  previousRoutes: [],
  setPreviousRoutes: (routes: string[]) => set({ previousRoutes: routes }),
}));

/**
 * Custom hook to keep track of the previous routes visited.
 *
 * @param {number} [size=3] - The maximum number of previous routes to keep track of.
 * @returns {string[]} - The previous routes visited. The last route visited is at the end of the array.
 *
 * @example
 * const previousRoutes = usePreviousRoutes(5);
 *
 * This hook will store the last 5 routes visited in the `previousRoutes` state.
 */
export function usePreviousRoutes(size: number = 3): string[] {
  const pathName = usePathname();

  React.useEffect(() => {
    const previousRoutes = useRouterStore.getState().previousRoutes;
    if (!pathName || pathName === previousRoutes.at(-1)) {
      return;
    }

    return () => {
      const currentRoutes = useRouterStore.getState().previousRoutes;
      const newRoutes = [...currentRoutes, pathName].slice(-size);
      useRouterStore.getState().setPreviousRoutes(newRoutes);
    };
  }, [pathName, size]);

  return useRouterStore(useShallow((state) => state.previousRoutes));
}
